/**
 * Console management utilities
 * Helps with controlling console output in different environments
 */

// Flag to track if console visibility has been initialized
let consoleInitialized = false;

/**
 * Initializes console visibility settings
 * Call this early in the application bootstrap process
 */
export function initializeConsoleVisibility(): void {
  if (consoleInitialized) return;
  
  try {
    // Store original console methods
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug
    };
    
    // Enable or disable console based on environment
    const isProduction = process.env.NODE_ENV === 'production';
    const isDebugEnabled = localStorage.getItem('debug') === 'true';
    
    // In production, we might want to disable some console methods unless debug is enabled
    if (isProduction && !isDebugEnabled) {
      // Keep error logging but make others no-ops
      console.log = function() {};
      console.info = function() {};
      console.debug = function() {};
      
      // Optionally keep warnings
      // console.warn = function() {};
    }
    
    // Add helper to restore original console
    (console as any).restoreConsole = function() {
      Object.assign(console, originalConsole);
      console.log('Console restored to original state');
    };
    
    // Add helper to enable debug mode
    (console as any).enableDebug = function() {
      localStorage.setItem('debug', 'true');
      (console as any).restoreConsole();
      console.log('Debug mode enabled');
    };
    
    consoleInitialized = true;
  } catch (error) {
    // If anything goes wrong, ensure console methods still work
    console.error('Error initializing console:', error);
  }
}

/**
 * Creates a namespaced logger for a specific component or module
 * @param namespace The namespace to prefix log messages with
 * @returns Object with logging methods
 */
export function createLogger(namespace: string) {
  return {
    log: (...args: any[]) => console.log(`[${namespace}]`, ...args),
    warn: (...args: any[]) => console.warn(`[${namespace}]`, ...args),
    error: (...args: any[]) => console.error(`[${namespace}]`, ...args),
    info: (...args: any[]) => console.info(`[${namespace}]`, ...args),
    debug: (...args: any[]) => console.debug(`[${namespace}]`, ...args)
  };
}

/**
 * Initialize console as soon as this module is loaded
 * This ensures console is properly configured before any other code runs
 */
initializeConsoleVisibility();
